import { QueryObserverResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useContext } from 'react';
import { useProcessGraph, useProcessMapping } from '../../api/process-client/process-client.hooks';
import type { GraphData, ProcessMapping } from '../../api/process-client/process-client.type';
import type { WorkPeriod } from '../../api/work-periods-client/work-periods-client.type';
import type { TaskTypeValue } from '../tasks-table/tasks-table.type';
import { ProcessContext } from './context/process.context';

/**
 * Returns the process scope from the ProcessContext.
 *
 * @returns {Object} The process scope object and setter.
 */
export const useProcessScope = () => {
  const { scope, setScope, rightScope, setRightScope, isComparisonView, setIsComparisonView } =
    useContext(ProcessContext);

  return {
    ...scope,
    setScope,
    rightScope,
    setRightScope,
    isComparisonView,
    setIsComparisonView,
  };
};

/**
 * Hook for fetching data in single view mode.
 * Retrieves the process mapping and graph data for a single team view.
 *
 * @param {Object} params - The parameters for fetching process data
 * @param {Object|null} params.workflow - The selected workflow object with id
 * @param {Object|null} params.team - The selected team object with id
 * @param {Object|null} params.board - The selected board object with id
 * @param {Object|null} params.workPeriod - The selected work period object with id
 * @param {TaskType|null} params.taskType - Type of tasks to filter by
 * @param {Object|null} params.epic - The selected epic with id
 * @param {Object|null} params.task - The selected task with id
 * @param {string|null} params.startDate - Start date for the date range filter
 * @param {string|null} params.endDate - End date for the date range filter
 * @param {boolean} params.useBloomfilterSections - Whether to use bloomfilter sections
 * @returns {Object} Object containing mapping, graphData and query objects
 */
const useSingleViewData = (params: {
  team?: { id: string } | null;
  board?: { id: string } | null;
  taskTypes: TaskTypeValue[] | null;
  epic?: { id: string } | null;
  task?: { id: string } | null;
  workflow?: { id: string } | null;
  startDate: string | null;
  endDate: string | null;
  useBloomfilterSections: boolean;
  workPeriod?: WorkPeriod | null;
}): {
  mapping: ProcessMapping | undefined;
  graphData: GraphData | undefined;
  query: QueryObserverResult<GraphData>;
} => {
  const { team, board, taskTypes, epic, task, workflow, startDate, endDate, useBloomfilterSections, workPeriod } =
    params;

  // Format dates properly for the API, using the same approach as in scope.tsx
  const formattedStartDate = startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined;
  const formattedEndDate = endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined;

  // Handle null values for taskType and epicId explicitly like in scope.tsx
  const formattedTaskTypes = taskTypes && taskTypes.length > 0 ? taskTypes : undefined;
  const formattedEpicId = epic?.id || undefined;
  const formattedTaskId = task?.id || undefined;

  // Mapping for single view
  const { mapping } = useProcessMapping({
    teamId: team?.id,
    workflowId: workflow?.id,
    options: {
      enabled: !!team?.id || !!workflow?.id,
    },
  });

  // Graph data for single view with proper handling of null values
  const { graphData, query } = useProcessGraph({
    teamId: team?.id,
    workflowId: workflow?.id,
    boardId: board?.id || undefined,
    workPeriodId: workPeriod?.id || undefined,
    taskTypes: formattedTaskTypes,
    epicId: formattedEpicId,
    taskId: formattedTaskId,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    useBloomfilterSections: useBloomfilterSections,
    options: {
      enabled: !!team?.id || !!workflow?.id,
      queryKey: [
        'process-graph',
        team?.id,
        workflow?.id,
        board?.id || null,
        workPeriod?.id || null,
        formattedEpicId,
        formattedTaskTypes,
        formattedTaskId,
        formattedStartDate,
        formattedEndDate,
        useBloomfilterSections,
      ] as const,
    },
  });

  return {
    mapping,
    graphData,
    query,
  };
};

export { useSingleViewData };
